<template>
    <v-card :loading="loading">
        <!-- Loading Overlay -->
        <v-overlay :value="loading" absolute z-index="999">
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="70"
                    width="7"
                    color="secondary"
                ></v-progress-circular>
                <div class="mt-4 white--text" style="font-size: 18px;">
                    {{ $t('LoadingResults') || 'Loading Results...' }}
                </div>
            </div>
        </v-overlay>

        <div class="card-title d-flex align-center justify-space-between">
            <span>{{ $t('SurveyResults') }}</span>
            <router-link :to="{ name: 'survey-list'}" custom>
                <v-btn fab x-small class="white--text" color="secondary" role="link" :title="$t('SurveyResults')">
                    <v-icon>mdi-arrow-left-circle</v-icon>
                </v-btn>
            </router-link>
        </div>
        <v-card-text>
            <div>
                <h2> {{title}}</h2>
                <!-- Filters. Laid out on the Vuetify grid alone - the previous markup mixed
                     float-start/float-end with grid columns, which stranded the action buttons
                     mid-row and squeezed the three checkboxes into md=4 inside an md=6 parent. -->
                <div class="container-fluid mt-4">
                    <v-row align="center">
                        <v-col cols="12" sm="6" md="3">
                            <DatePicker dense outlined v-model.lazy="startDate" :label="$t('StartDate')" />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                            <DatePicker dense outlined v-model.lazy="expiryDate" :label="$t('ExpiryDate')" />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex flex-wrap justify-md-end align-center results-actions">
                            <v-btn tile :loading="loadingExport" :disabled="loadingExport" color="primary darken-3" class="white--text" @click="Search">
                                <v-icon start dark size="20">mdi-search-web</v-icon>
                                {{ $t('SEARCH') }}
                            </v-btn>
                            <v-btn tile :loading="loadingExport" :disabled="loadingExport" color="green darken-3" class="white--text" @click="exportToExcel">
                                <v-icon start dark size="20">mdi-microsoft-excel</v-icon>
                                {{ $t('EXPORT') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" sm="4">
                            <v-checkbox dense hide-details class="mt-0" v-model.lazy="trickyQuestionValidated" :label="$t('TrickyQuestionValidated')" />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-checkbox dense hide-details class="mt-0" v-model.lazy="durationValidated" :label="$t('DurationValidated')" />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-checkbox dense hide-details class="mt-0" v-model.lazy="patternValidated" :label="$t('PatternValidated')" />
                        </v-col>
                    </v-row>
                </div>
                <!--<v-btn v-if="isShow" tile :loading="loadingExport" :disabled="loadingExport" color="green darken-3" class="float-end white--text" @click="isExport=true;isShow=false;">
                <v-icon start dark size="25">
                    mdi-microsoft-excel
                </v-icon>
                {{ $t('EXPORTTOEXCEL') }}
            </v-btn>-->
            </div>
        </v-card-text>
        <v-card-text v-if="results.length > 0">
            <div class="d-flex justify-space-between align-center mb-2">
                <div class="text-subtitle-1 font-weight-medium">
                    {{ tr('TotalVotes', 'إجمالي المشاركات', 'Total votes') }}: {{ totalCount }}
                </div>
                <v-btn
                    v-if="selectedParticipants.length > 0"
                    small
                    color="error"
                    class="white--text"
                    @click="confirmDeleteSelected"
                >
                    <v-icon left>mdi-delete</v-icon>
                    {{ tr('DeleteSelected', 'حذف المحدد', 'Delete selected') }} ({{ selectedParticipants.length }})
                </v-btn>
            </div>
            <GridWithServerPaging :headers="visibleHeaders" :items="results" :total="totalCount" disable-sort class="mt-4" @onChange="listSurveyresults">

                <template v-slot:[`header.select`]="{}">
                    <v-checkbox
                        :input-value="selectedParticipants.length === results.length && results.length > 0"
                        :indeterminate="selectedParticipants.length > 0 && selectedParticipants.length < results.length"
                        hide-details
                        dense
                        class="ma-0 pa-0"
                        @change="val => selectedParticipants = val ? results.map(r => r.id) : []"
                    ></v-checkbox>
                </template>
                <template v-slot:[`item.select`]="{ item }">
                    <v-checkbox
                        v-model="selectedParticipants"
                        :value="item.id"
                        hide-details
                        dense
                        class="ma-0 pa-0"
                    ></v-checkbox>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <b class="primary--text">{{ item.user }}</b>
                </template>
                <!-- Validation flags read as ✓/✕ rather than the raw strings "true"/"false" -->
                <template v-slot:[`item.trickyQuestionValidated`]="{ item }">
                    <v-icon small
                            :color="isTrue(item.trickyQuestionValidated) ? 'success' : 'grey lighten-1'"
                            :title="isTrue(item.trickyQuestionValidated) ? $t('Yes') : $t('No')">
                        {{ isTrue(item.trickyQuestionValidated) ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                    </v-icon>
                </template>
                <template v-slot:[`item.durationValidated`]="{ item }">
                    <v-icon small
                            :color="isTrue(item.durationValidated) ? 'success' : 'grey lighten-1'"
                            :title="isTrue(item.durationValidated) ? $t('Yes') : $t('No')">
                        {{ isTrue(item.durationValidated) ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                    </v-icon>
                </template>
                <template v-slot:[`item.patternValidated`]="{ item }">
                    <v-icon small
                            :color="isTrue(item.patternValidated) ? 'success' : 'grey lighten-1'"
                            :title="isTrue(item.patternValidated) ? $t('Yes') : $t('No')">
                        {{ isTrue(item.patternValidated) ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                    </v-icon>
                </template>
                <template v-slot:[`item.longitude`]="{ item }">
                    {{ item.longitude }}
                </template>
                <template v-slot:[`item.latitude`]="{ item }">
                    {{ item.latitude }}
                </template>
                <!-- UploadFiles answers are stored as comma-joined attachment ids; render each
                     one as a view/download action instead of printing the raw ids -->
                <template v-for="col in uploadFileColumns" v-slot:[`item.${col}`]="{ item }">
                    <div :key="col" class="d-flex flex-wrap align-center">
                        <v-chip v-for="(attId, i) in parseAttachmentIds(item[col])"
                                :key="attId"
                                small
                                class="ma-1"
                                color="primary"
                                outlined
                                @click="openAttachment(attId)">
                            <v-icon small left>mdi-paperclip</v-icon>
                            {{ $t('Attachment') }} {{ i + 1 }}
                        </v-chip>
                    </div>
                </template>
                <!-- Kept on one line: the cell used to wrap, pushing the delete icon into the
                     neighbouring row. -->
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex align-center actions-cell">
                        <v-btn v-if="canEditAnswer" color="secondary" class="white--text" tile small :to="`/form/${$route.params.id}?participantId=${item.id}&editMode=true`">
                            {{ $t("EditSurveyAnswer") }}
                        </v-btn>
                        <v-btn
                            small
                            icon
                            color="error"
                            :title="$t('Delete')"
                            @click="confirmDeleteSingle(item)"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>
            </GridWithServerPaging>
            <FileViewer v-model="attachmentDialog" :query="attachmentQuery" is-dialog is-client-side :title="$t('Attachment')" />
        </v-card-text>
        <v-card-text v-else-if="!loading" class="text-center py-10">
            <v-icon size="56" color="grey lighten-1">mdi-clipboard-text-off-outline</v-icon>
            <div class="text-subtitle-1 grey--text text--darken-1 mt-3">{{ $t('NoItemsToShow') }}</div>
        </v-card-text>

        <!-- Replaces window.confirm(): unstyled browser chrome that ignores the app's RTL
             Arabic presentation and blocks the JS thread while open. -->
        <v-dialog v-model="deleteDialog" max-width="420" persistent>
            <v-card>
                <v-card-title class="text-h6">
                    {{ tr('ConfirmDelete', 'تأكيد الحذف', 'Confirm delete') }}
                </v-card-title>
                <v-card-text>
                    <template v-if="deleteTarget">
                        {{ tr('ConfirmDeleteRow', 'سيتم حذف هذه المشاركة نهائياً. لا يمكن التراجع عن هذا الإجراء.', 'This vote will be permanently deleted. This cannot be undone.') }}
                    </template>
                    <template v-else>
                        {{ tr('ConfirmDeleteSelected', 'سيتم حذف المشاركات المحددة نهائياً. لا يمكن التراجع عن هذا الإجراء.', 'The selected votes will be permanently deleted. This cannot be undone.') }}
                        <div class="mt-2 font-weight-medium">{{ selectedParticipants.length }}</div>
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="deleting" @click="deleteDialog = false">{{ $t('Cancel') }}</v-btn>
                    <v-btn color="error" class="white--text" :loading="deleting" @click="performDelete">
                        <v-icon left small>mdi-delete</v-icon>
                        {{ $t('Delete') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
    import DatePicker from '@/components/controls/DatePicker.vue'
    import SurveyService from '@/services/SurveyService'
    import UsersService from '@/services/UsersService.js'
    import GridWithServerPaging from '@/components/controls/GridWithServerPaging.vue'
    import FileViewer from '@/components/controls/FileViewer.vue'
    import AttachmentsService from '@/services/AttachmentsService.js'
    import { downloadFile } from '@/helpers/downloader.js'
    import { useStripHtml } from '@/helpers/common.js'
    import { FormMethodEnum } from '@/helpers/constants/enumerations.js'

    import * as XLSX from 'xlsx'
    import { saveAs } from 'file-saver'

    export default {
        components: {
            GridWithServerPaging, DatePicker, FileViewer
        },

        data() {
            return {
                isExport: false,
                
                isShow:true,
                loadingExport: false,
                loading: true,
                surveyResult: null,
                options: {
                    page: 1,
                    itemsPerPage: 10
                },
                totalCount: 10,
                selectedParticipants: [],
                headers: [],
                // header `value`s (q{questionId}) belonging to UploadFiles questions, so their
                // cells can render the stored attachment ids as view/download actions
                uploadFileColumns: [],
                attachmentDialog: false,
                attachmentQuery: null,
                deleteDialog: false,
                deleteTarget: null,
                deleting: false,
                results: [],
                title: '',
                canEditAnswer: false,
                startDate: '',
                expiryDate: '',
                trickyQuestionValidated: null,
                durationValidated:null,
                patternValidated:null
            }
        },

        computed: {
            // Longitude/Latitude are only populated for GIS-enabled surveys. Hiding them when
            // every row is blank buys back two columns of width on an already-wide table.
            visibleHeaders() {
                const hasGeo = this.results.some(r => this.hasValue(r.longitude) || this.hasValue(r.latitude))
                if (hasGeo) return this.headers
                return this.headers.filter(h => h.value !== 'longitude' && h.value !== 'latitude')
            }
        },

        created() {
            this.listSurveyresults()
            this.canEditAnswerPermission()
        },

        beforeDestroy() {
            this.releaseAttachmentUrl()
        },

        methods: {
            // $t() returns the key itself when a translation is missing, so the codebase's
            // `$t('X') || 'fallback'` idiom never fires - that's why the page rendered the literal
            // strings "LoadingResults" and "TotalVotes". $te() actually tests for existence.
            tr(key, ar, en) {
                if (this.$te(key)) return this.$t(key)
                return this.$i18n && this.$i18n.locale === 'en' ? en : ar
            },
            isTrue(value) {
                return value === true || value === 'true'
            },
            hasValue(value) {
                return value !== null && value !== undefined && String(value).trim() !== ''
            },
            // An UploadFiles answer is stored as a comma-joined list of Attachment ids ("2312,2313")
            parseAttachmentIds(value) {
                if (value == null || value === '') return []
                return String(value)
                    .split(',')
                    .map(id => id.trim())
                    .filter(id => id !== '' && !isNaN(id))
            },
            // Resolve the signed query, fetch the bytes, and hand FileViewer a client-side blob
            // URL. We deliberately don't pass the raw query: VUE_APP_VIEWER hard-codes the
            // elibraryattachments endpoint, so the pdf.js viewer would fetch these ids from the
            // wrong controller (ElibraryAttachment, not Attachment) and render an empty document.
            openAttachment(attachmentId) {
                AttachmentsService.getAttachment(attachmentId).then(response => {
                    const query = response.data
                    if (!query) {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        return null
                    }

                    // Type the viewer can't show (anything outside .pdf/.doc/.docx/.png/.jpg/.jpeg).
                    // Hand FileViewer the raw query and let its existing download path save it
                    // with the proper filename from Content-Disposition.
                    if (query.indexOf('norender=true') !== -1) {
                        this.releaseAttachmentUrl()
                        this.attachmentQuery = query
                        this.attachmentDialog = true
                        return null
                    }

                    // Viewer-supported type. The storage layer converts doc/docx/images to PDF
                    // and watermarks them, so the body is ALWAYS a PDF here - but the response
                    // Content-Type still reflects the original file (e.g. the docx mime), which
                    // would stop the iframe rendering. Force application/pdf on the blob.
                    return AttachmentsService.download(query).then(res => {
                        this.releaseAttachmentUrl()
                        const pdfBlob = new Blob([res.data], { type: 'application/pdf' })
                        this.attachmentQuery = window.URL.createObjectURL(pdfBlob)
                        this.attachmentDialog = true
                    })
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                })
            },
            // Free the previous object URL. Deliberately leaves attachmentQuery as-is - FileViewer
            // watches `query` and treats a null/unsupported value as "download it", so clearing it
            // here would fire a spurious download request.
            releaseAttachmentUrl() {
                if (this.attachmentQuery && this.attachmentQuery.indexOf('blob:') === 0) {
                    window.URL.revokeObjectURL(this.attachmentQuery)
                }
            },
            Search() {
                this.listSurveyresults()
            },
            canEditAnswerPermission() {
                UsersService.listCurrentUserPermissions().then((res) => {
                    if (res.data) {
                        this.canEditAnswer = res.data.some(userPermission => userPermission.name === 'EditSurveyAnswer')

                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    //this.loading = false
                })
            },
            exportToExcelFrontEnd() {
                const wsData = [
                    this.headers.map(header => header.text),
                    ...this.results.map(item => {
                        return this.headers.map(header => {
                            if (header.value in item) {
                                return item[header.value]
                            } else {
                                return ''
                            }
                        })
                    })
                ]
                const ws = XLSX.utils.aoa_to_sheet(wsData)
                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
                XLSX.writeFile(wb, 'export.xlsx')
            },
            exportToExcel() {
                this.loadingExport = true
                const fromDate = this.startDate
                const toDate = this.expiryDate
                SurveyService.exportToExcel(this.$route.params.id, fromDate, toDate, this.trickyQuestionValidated, this.durationValidated, this.patternValidated).then(res => {
                    downloadFile(res)
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loadingExport = false
                })
            },
            exportToExcelForEdit() {
                this.loadingExport = true
                const fromDate = this.startDate
                const toDate = this.expiryDate
                SurveyService.exportToExcelForEdit(this.$route.params.id, fromDate, toDate, this.trickyQuestionValidated, this.durationValidated, this.patternValidated).then(res => {
                    downloadFile(res)
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loadingExport = false
                })
            },

            listSurveyresults(options) {
                if (this.$route.params.id) {
                    this.loading = true

                    const page = options != undefined || options == false ? options.page : 1
                    const pageSize = options != undefined ? options.itemsPerPage : 10

                    SurveyService.listSurveyResults(this.$route.params.id, page, pageSize, this.trickyQuestionValidated, this.durationValidated, this.patternValidated).then(res => {
                        if (res.data) {
                            this.headers = []
                            this.uploadFileColumns = []
                            this.results = []
                            this.title = useStripHtml(res.data.title)
                            this.totalCount = res.data.totalCount
                            this.selectedParticipants = []
                            this.headers.push({ text: '', value: 'select', sortable: false })
                            this.headers.push({ text: this.$t('Id'), value: 'id' })
                            this.headers.push({ text: this.$t('Username'), value: 'user' })
                            this.headers.push({ text: this.$t('Longitude'), value: 'longitude' })
                            this.headers.push({ text: this.$t('Latitude'), value: 'latitude' })
                            this.headers.push({ text: this.$t('AverageCompletion'), value: 'averageCompletion' })
                            this.headers.push({ text: this.$t('TrickyQuestionValidated'), value: 'trickyQuestionValidated' })
                            this.headers.push({ text: this.$t('DurationValidated'), value: 'durationValidated' })
                            this.headers.push({ text: this.$t('PatternValidated'), value: 'patternValidated' })
                            res.data.questions.map(x => {
                                const questionName = x.name ? useStripHtml(x.name) : x.name

                                if (x.parentId == null && x.questionTypeId != FormMethodEnum.PredefineDropdown && x.questionTypeId != FormMethodEnum.ContactForm) {
                                    var subquestions = res.data.questions.filter(z => z.parentId == x.id)
                                    if (x.questionTypeId == FormMethodEnum.UploadFiles)
                                        this.uploadFileColumns.push(`q${x.id}`)
                                    if (subquestions.length == 0 && this.headers.filter(x => x.value == `q${x.id}`).length == 0)
                                        this.headers.push({ text: questionName, value: `q${x.id}` })
                                    else {
                                        subquestions.forEach(a => {
                                            const subQuestionName = a.name ? useStripHtml(a.name) : a.name
                                            if (this.headers.filter(x => x.value == `q${a.id}`).length == 0)
                                                this.headers.push({ text: `${questionName}:${subQuestionName}`, value: `q${a.id}` })
                                        }
                                        )
                                    }
                                }

                            })
                            if (res.data.gisintegration == true) {
                                this.headers.push({ text: this.$t('municipality'), value: 'municipality' })
                                this.headers.push({ text: this.$t('subMunicipality'), value: 'subMunicipality' })
                                this.headers.push({ text: this.$t('district'), value: 'district' })
                                this.headers.push({ text: this.$t('street'), value: 'street' })

                            }
                            for (var i = 0; i < res.data.surveyParticipants.length; i++) {
                                const obj = {}
                                let item = res.data.surveyParticipants[i]
                                for (var j = 0; j < item.items.length; j++) {
                                    let subItem = item.items[j]
                                    let ref = `q${subItem.questionId}`
                                    var answers = item.items.filter(x => x.questionId == subItem.questionId)
                                    if (answers.length > 1) {
                                        var ans = ''
                                        answers.forEach(e =>
                                            ans += ', ' + e.answer
                                        )
                                        if (ans != '') {
                                            ans = ans.substr(1)
                                        }
                                        obj[ref] = ans
                                    }
                                    else {
                                        if (subItem.isPredefined) {
                                            var records = subItem.answer.split('|')
                                            records.map(r => {
                                                var q = r.split('-')[0]
                                                var a = r.split('-')[1]
                                                if (this.headers.filter(x => x.value == q).length == 0) {
                                                    this.headers.push({ text: useStripHtml(q), value: q })

                                                }
                                                obj[q] = a
                                            })
                                        }
                                        else {
                                            obj[ref] = subItem.answer
                                        }
                                        if (res.data.questions.find(x => x.id == subItem.questionId && x.questionTypeId == FormMethodEnum.ContactForm)) {
                                            
                                                var answer = subItem.answer
                                                
                                                try {
                                                    var jsonData = JSON.parse(answer)
                                                    Object.entries(jsonData).forEach(([key, value]) => {
                                                        if(this.headers.filter(x => x.value == 'cf_'+key).length == 0) {
                                                            this.headers.push({ text: key, value: 'cf_' + key })

                                                        }
                                                        obj['cf_' + key] = value
                                                    })
                                                }
                                                catch {

                                                }
                                        }
                                    }
                                }
                                obj.id = `${item.id}`
                                obj.user = `${item.fullName || item.sourceIp}`
                                obj.longitude = `${item.longitude}`
                                obj.latitude = `${item.latitude}`
                                if (item.averageCompletion) { obj.averageCompletion = `${item.averageCompletion.split('.')[0]}` }
                               
                                obj.trickyQuestionValidated = item.trickyQuestionValidated
                                obj.durationValidated = item.durationValidated
                                obj.patternValidated = item.patternValidated
                                if (res.data.gisintegration == true) {
                                    obj.municipality = !item.locationData[0].locData || item.locationData[0].locData == '' ? '' : `${item.locationData[0].locData.split('|')[0].split(':')[1]}`
                                    obj.subMunicipality = !item.locationData[0].locData || item.locationData[0].locData == '' ? '' : `${item.locationData[0].locData.split('|')[1].split(':')[1]}`
                                    obj.district = !item.locationData[0].locData || item.locationData[0].locData == '' ? '' : `${item.locationData[0].locData.split('|')[2].split(':')[1]}`
                                    obj.street = !item.locationData[0].locData || item.locationData[0].locData == '' ? '' : `${item.locationData[0].locData.split('|')[3].split(':')[1]}`

                                }
                                this.results.push(obj)
                            }
                            if (this.canEditAnswer)
                                this.headers.push({ text: this.$t('Actions'), value: 'actions' })
                        }
                    }).catch((err) => {
                        
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                       this.loading = false
                    })
                }
            },
            confirmDeleteSelected() {
                if (!this.selectedParticipants.length) return
                this.deleteTarget = null
                this.deleteDialog = true
            },
            confirmDeleteSingle(item) {
                this.deleteTarget = item
                this.deleteDialog = true
            },
            async performDelete() {
                const ids = this.deleteTarget ? [this.deleteTarget.id] : this.selectedParticipants.slice()
                if (!ids.length) {
                    this.deleteDialog = false
                    return
                }
                this.deleting = true
                try {
                    await SurveyService.deleteSurveyResults(this.$route.params.id, ids)
                    this.selectedParticipants = this.selectedParticipants.filter(id => ids.indexOf(id) === -1)
                    this.deleteDialog = false
                    this.deleteTarget = null
                    this.listSurveyresults(this.options)
                } catch (e) {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                } finally {
                    this.deleting = false
                }
            }
        },
    }
</script>

<style scoped>
    /* Space the search/export pair without margin hacks on each button */
    .results-actions {
        gap: 8px;
    }

    /* Keep the edit button and delete icon on one line so the cell can't wrap into
       the neighbouring row */
    .actions-cell {
        white-space: nowrap;
        gap: 4px;
    }

    .odd {
        background: #fff !important
    }

    .custom-table {
        border-collapse: collapse;
        width: 100%;
    }

        .custom-table thead th {
            border: 1px solid #ccc;
            color: #fff !important;
            font-weight: normal !important;
            font-size: 12px !important
        }

    tbody tr:nth-of-type(2n) {
        background-color: #eceded !important;
    }
</style>